import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  Modal,
  Spinner,
} from "react-bootstrap";

import {
  canApproveRegistration,
  canEditFiringOrder,
  canEditRallyNumber,
  canUnapproveRegistration,
  flattenRegistrationDetails,
  getDriverImageUrl,
  getDriverName,
  getFiringOrder,
  getInheritedConsentInfo,
  getRallyNumber,
  getStatusBadgeVariant,
  isQualifiedRegistration,
} from "../utils/competitors.utils";

const CompetitorEditModal = ({
  show,
  registration,
  categories = [],
  isLoading = false,
  canManage = false,
  approving = false,
  uploading = false,
  savingFiringOrder = false,
  savingRallyNumber = false,
  fieldErrors = {},
  onClose,
  onApprove,
  onUnapprove,
  onDownloadConsent,
  onDownloadSignedConsent,
  onUploadSignedConsent,
  onOpenInspection,
  onSaveFiringOrder,
  onSaveRallyNumber,
}) => {
  const status = registration?.status || "";
  const consent = registration
    ? getInheritedConsentInfo(registration, categories)
    : null;
  const imageUrl = registration ? getDriverImageUrl(registration) : "";
  const id = registration?._id || registration?.id;
  const details = flattenRegistrationDetails(registration);

  const [firingOrderDraft, setFiringOrderDraft] = useState("");
  const [rallyNumberDraft, setRallyNumberDraft] = useState("");

  useEffect(() => {
    if (!show || !registration) return;
    const order = getFiringOrder(registration);
    setFiringOrderDraft(order === 0 || order ? String(order) : "");
    setRallyNumberDraft(getRallyNumber(registration) || "");
  }, [show, registration]);

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit competitor</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading && !registration ? (
          <div className="d-flex justify-content-center py-4">
            <Spinner animation="border" />
          </div>
        ) : !registration ? (
          <p className="text-muted mb-0">Registration not found.</p>
        ) : (
          <>
            <div className="d-flex gap-3 align-items-start mb-4">
              <div
                className="d-flex align-items-center justify-content-center overflow-hidden border rounded"
                style={{ width: 72, height: 72, flexShrink: 0 }}
              >
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={getDriverName(registration)}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <span className="text-muted small">No photo</span>
                )}
              </div>
              <div>
                <h5 className="mb-1">{getDriverName(registration)}</h5>
                <div className="d-flex flex-wrap gap-2">
                  <Badge bg={getStatusBadgeVariant(status)}>
                    {status || "-"}
                  </Badge>
                  <Badge bg={consent?.isSigned ? "success" : "secondary"}>
                    {consent?.isSigned ? "Consent signed" : "Consent not signed"}
                  </Badge>
                  <Badge
                    bg={
                      isQualifiedRegistration(registration)
                        ? "success"
                        : "secondary"
                    }
                  >
                    {isQualifiedRegistration(registration)
                      ? "Qualified"
                      : "Not qualified"}
                  </Badge>
                </div>
              </div>
            </div>

            <Alert variant="light" className="border mb-4">
              Consent PDFs are category-level. Uploading a new signed PDF
              replaces the previous one for every registration in this category.
            </Alert>

            <div className="row mb-4">
              <div className="col-md-6 mb-3">
                <Form.Group>
                  <Form.Label>Firing order</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="number"
                      min={1}
                      step={1}
                      value={firingOrderDraft}
                      disabled={
                        !canManage ||
                        !canEditFiringOrder(registration) ||
                        savingFiringOrder
                      }
                      onChange={(e) => setFiringOrderDraft(e.target.value)}
                      isInvalid={Boolean(fieldErrors.firing_order)}
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      disabled={
                        !canManage ||
                        !canEditFiringOrder(registration) ||
                        savingFiringOrder
                      }
                      onClick={() =>
                        onSaveFiringOrder(
                          registration,
                          firingOrderDraft.trim() === ""
                            ? null
                            : Number(firingOrderDraft)
                        )
                      }
                    >
                      {savingFiringOrder ? "Saving..." : "Save"}
                    </Button>
                  </div>
                  {fieldErrors.firing_order ? (
                    <div className="text-danger small mt-1">
                      {fieldErrors.firing_order}
                    </div>
                  ) : (
                    <Form.Text className="text-muted">
                      Requires approved + qualified pipeline stage.
                    </Form.Text>
                  )}
                </Form.Group>
              </div>

              <div className="col-md-6 mb-3">
                <Form.Group>
                  <Form.Label>Rally number</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="text"
                      value={rallyNumberDraft}
                      disabled={
                        !canManage ||
                        !canEditRallyNumber(registration) ||
                        savingRallyNumber
                      }
                      onChange={(e) => setRallyNumberDraft(e.target.value)}
                      isInvalid={Boolean(fieldErrors.rally_number)}
                    />
                    <Button
                      type="button"
                      variant="outline-primary"
                      disabled={
                        !canManage ||
                        !canEditRallyNumber(registration) ||
                        savingRallyNumber
                      }
                      onClick={() =>
                        onSaveRallyNumber(registration, rallyNumberDraft)
                      }
                    >
                      {savingRallyNumber ? "Saving..." : "Save"}
                    </Button>
                  </div>
                  {fieldErrors.rally_number ? (
                    <div className="text-danger small mt-1">
                      {fieldErrors.rally_number}
                    </div>
                  ) : (
                    <Form.Text className="text-muted">
                      Requires firing order to be set first.
                    </Form.Text>
                  )}
                </Form.Group>
              </div>
            </div>

            <h6 className="mb-3">Full details</h6>
            <div className="row">
              {details.map((row) => (
                <div className="col-md-6 mb-3" key={`${row.label}-${row.value}`}>
                  <div className="text-muted small mb-1">{row.label}</div>
                  <div>{row.value || "-"}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="flex-wrap gap-2">
        {consent?.hasConsentTemplate ? (
          <Button
            variant="outline-secondary"
            onClick={() => onDownloadConsent(registration)}
          >
            Download consent
          </Button>
        ) : null}
        {consent?.signedUrl ? (
          <Button
            variant="outline-secondary"
            onClick={() => onDownloadSignedConsent(consent.signedUrl)}
          >
            Download signed consent
          </Button>
        ) : null}
        {canManage && consent?.categoryId ? (
          <Button
            variant="outline-primary"
            disabled={uploading}
            onClick={() => onUploadSignedConsent(registration)}
          >
            {uploading
              ? "Uploading..."
              : consent.isSigned
                ? "Replace signed consent"
                : "Upload signed consent"}
          </Button>
        ) : null}
        {canManage && registration ? (
          <Button
            variant="outline-primary"
            onClick={() => onOpenInspection(registration)}
          >
            {registration.inspection || registration.inspected
              ? "Edit inspection"
              : "Open inspection"}
          </Button>
        ) : null}
        {canManage && canApproveRegistration(status) ? (
          <Button
            variant="success"
            disabled={approving || !id}
            onClick={() => onApprove(registration)}
          >
            {approving ? "Approving..." : "Approve"}
          </Button>
        ) : null}
        {canManage && canUnapproveRegistration(status) ? (
          <Button
            variant="warning"
            disabled={approving || !id}
            onClick={() => onUnapprove(registration)}
          >
            {approving ? "Updating..." : "Set pending"}
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CompetitorEditModal;
